import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  HStack,
  Button,
  useColorModeValue,
  Icon,
  useDisclosure,
  Fade,
  Badge,
  Skeleton,
  Grid,
  GridItem,
  Wrap,
  WrapItem,
  Tooltip,
  Flex,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Link as ChakraLink,
  IconButton,
  ButtonGroup
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom'; // Aliased Link
import { MapPin, Funnel, MagnifyingGlass, X, TextAa, Info as InfoIcon } from '@phosphor-icons/react'; // Added InfoIcon
import { useGeocoding } from '../hooks/useGeocoding';
import SearchBar from '../components/SearchBar';
import SiteCard from '../components/SiteCard';
import SearchFilters from '../components/SearchFilters';
import ImagePreviewModal from '../components/ImagePreviewModal';
import { getAllSites, getSitesByState } from '../firebase/siteQueries';
import { 
  getSearchableSites, 
  getSearchableSitesByState, 
  searchSearchableSitesByName,
  getSearchableSiteFeatures 
} from '../firebase/searchableSiteQueries';
// AUTO_DETECT_MAPPING is used by mapFeature
import { mediaHasFeature, normalizeFeature, mapFeature } from '../utils/featureUtils';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    materialType: '',
    features: [], // Will store standardized feature IDs
    era: '',
    verificationStatus: '',
    countries: [],
    featureFilterLogic: 'AND' // Added
  });
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedSiteMedia, setSelectedSiteMedia] = useState([]);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure(); // For ImagePreviewModal
  const { isOpen: isInfoModalOpen, onOpen: onInfoModalOpen, onClose: onInfoModalClose } = useDisclosure(); // For Info Modal
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [featuresMetadata, setFeaturesMetadata] = useState({}); // Keys will be standardized IDs
  const [gridColumns, setGridColumns] = useState(2); // Added for layout control
  
  const location = searchParams.get('location');
  const name = searchParams.get('name');
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const badgeBg = useColorModeValue('ancient.subtle', 'gray.700');
  const badgeTextColor = useColorModeValue('gray.800', 'white');
  const [sites, setSites] = useState([]);
  const [error, setError] = useState(null);
  const [availableCountries, setAvailableCountries] = useState([]);
  const { reverseGeocode } = useGeocoding();

  // Request user's location and update search params
  /* useEffect(() => {
    if (!location && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const addressInfo = await reverseGeocode(
            position.coords.latitude,
            position.coords.longitude
          );
          
          if (addressInfo) {
            const userState = addressInfo.components.find(
              component => component.types.includes('administrative_area_level_1')
            )?.long_name;
            
            if (userState) {
              setSearchParams({ location: userState });
            }
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
        },
        { enableHighAccuracy: true }
      );
    }
  }, []); */

  useEffect(() => {
    const fetchSites = async () => {
      setIsLoading(true);
      try {
        let result;
        if (name) {
          // Search by site name using searchable sites
          result = await searchSearchableSitesByName(name);
        } else if (location) {
          // Search by location using searchable sites
          result = await getSearchableSitesByState(location);
        } else {
          // Get all searchable sites
          result = await getSearchableSites();
        }
        
        if (result.success) {
          setSites(result.data);
          setError(null);
          
          // Extract feature metadata from searchable sites data
          const featureMap = new Map();
          result.data.forEach(site => {
            // Process siteLevelAllImageFeatureTags
            if (Array.isArray(site.siteLevelAllImageFeatureTags)) {
              site.siteLevelAllImageFeatureTags.forEach(rawFeatureId => {
                // Standardize the ID using mapFeature (which handles snake_case)
                const { featureId: standardId, label, category } = mapFeature({ class: rawFeatureId, source: 'system', confidence: 1.0 });
                if (standardId && !featureMap.has(standardId)) {
                  featureMap.set(standardId, { 
                    id: standardId, // Store standardized ID
                    label: label || standardId, // Use mapped label
                    category: category || 'uncategorized', // Store category information
                    source: 'system' 
                  });
                }
              });
            }
            
            // Process searchableMediaItems featureTags
            if (Array.isArray(site.searchableMediaItems)) {
              site.searchableMediaItems.forEach(mediaItem => {
                if (Array.isArray(mediaItem.featureTags)) {
                  mediaItem.featureTags.forEach(rawFeatureId => {
                    const { featureId: standardId, label, category } = mapFeature({ class: rawFeatureId, source: 'system', confidence: 1.0 });
                    if (standardId && !featureMap.has(standardId)) {
                      featureMap.set(standardId, { 
                        id: standardId, // Store standardized ID
                        label: label || standardId, // Use mapped label
                        category: category || 'uncategorized', // Store category information
                        source: 'system'
                      });
                    }
                  });
                }
              });
            }
          });
          
          setFeaturesMetadata(Object.fromEntries(featureMap));
          
          // Extract unique countries from the site data
          const countriesFromSites = result.data.map(site => {
            // Check both site.country and site.location.country
            return site.country || site.location?.country;
          });
          
          const uniqueCountries = [...new Set(
            countriesFromSites.filter(country => country && country.trim() !== '')
          )].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
          setAvailableCountries(uniqueCountries);
        } else {
          setError('Failed to fetch sites');
          setSites([]);
          setAvailableCountries([]);
        }
      } catch (err) {
        console.error('Error fetching sites:', err);
        setError('An error occurred while fetching sites');
        setSites([]);
      }
      
      setTimeout(() => {
        setIsLoading(false);
      }, 600);
    };
    fetchSites();
  }, [location, name]);

  // Check if a prediction matches a feature - used for compatibility with existing code
  const predictionMatchesFeature = (prediction, featureId) => {
    const normalizedFeatureId = normalizeFeature(featureId);
    if (prediction.source === 'auto') {
      const mappedFeature = AUTO_DETECT_MAPPING[prediction.class.toLowerCase()];
      if (mappedFeature) {
        return normalizeFeature(mappedFeature) === normalizedFeatureId;
      }
      return normalizeFeature(prediction.class) === normalizedFeatureId;
    }
    return normalizeFeature(prediction.class) === normalizedFeatureId; // This function is less used now
  };

  // New helper: Checks if a media item contains a specific standardized feature
  const mediaContainsStandardizedFeature = (mediaItem, standardFeatureId) => {
    if (!mediaItem?.predictions || !Array.isArray(mediaItem.predictions) || !standardFeatureId) return false;
    
    return mediaItem.predictions.some(pred => {
      if (!pred || !pred.class) return false;
      if (pred.source === 'auto' && pred.confidence < 0.05) return false; // Consistent low confidence skip

      const { featureId: mappedPredFeatureId } = mapFeature(pred);
      return mappedPredFeatureId === standardFeatureId;
    });
  };
  
  // Check if a site has a feature (uses standardized IDs)
  const siteHasFeature = (site, standardFeatureId) => {
    // For searchable sites, use siteLevelAllImageFeatureTags for efficient checking
    if (Array.isArray(site.siteLevelAllImageFeatureTags)) {
      return site.siteLevelAllImageFeatureTags.includes(standardFeatureId);
    }
    // Fallback to legacy media array if available
    return Array.isArray(site.media) && site.media.some(mediaItem => mediaContainsStandardizedFeature(mediaItem, standardFeatureId));
  };

  // Helper function to check if site's dates match era
  const siteMatchesEra = (site, era) => {
    if (!era) return true;
    
    // Check construction periods first
    if (site.constructionPeriods?.length > 0) {
      return site.constructionPeriods.some(period => {
        const startMatch = dateMatchesEra(period.startEra, era);
        const endMatch = dateMatchesEra(period.endEra, era);
        return startMatch || endMatch;
      });
    }
    
    // Fall back to site-level dates
    return dateMatchesEra(site.startEra, era) || 
           dateMatchesEra(site.endEra, era);
  };

  // Helper function to check if a date matches an era
  const dateMatchesEra = (date, era) => {
    if (!date) return false;
    const num = parseInt(date);
    if (isNaN(num)) return false;
    
    const isBCE = date.toLowerCase().includes('bce');
    const year = isBCE ? -num : num;
    
    // Era date ranges
    const eraRanges = {
      prehistoric: [-100000, -3000], // Before 3000 BCE
      ancient: [-3000, 500], // 3000 BCE to 500 CE
      classical: [-500, 500], // 500 BCE to 500 CE
      medieval: [500, 1500], // 500 CE to 1500 CE
      modern: [1500, 2100] // 1500 CE onwards
    };

    const range = eraRanges[era];
    return range && year >= range[0] && year <= range[1];
  };

  // Filter sites based on criteria
  const applyFilters = (sites) => {
    if (!Array.isArray(sites)) return [];
    return sites.filter(site => {
      // Feature filtering with AND/OR logic (uses standardized feature IDs)
      if (filters.features?.length > 0) {
        if (filters.featureFilterLogic === 'OR') {
          // OR: site must have at least one of the selected features
          const siteMatchesAnyFeature = filters.features.some(stdId => siteHasFeature(site, stdId));
          if (!siteMatchesAnyFeature) return false;
        } else { // AND (default)
          // AND: site must have all of the selected features
          const siteMatchesAllFeatures = filters.features.every(stdId => siteHasFeature(site, stdId));
          if (!siteMatchesAllFeatures) return false;
        }
      }
      if (filters.era && !siteMatchesEra(site, filters.era)) return false;
      // verificationStatus filter seems to be removed from SearchElasticsearch, consider if needed
      // if (filters.verificationStatus && site.verificationStatus !== filters.verificationStatus) return false; 
      if (filters.countries?.length > 0) {
        const siteCountry = site.country || site.location?.country;
        if (!siteCountry || !filters.countries.includes(siteCountry)) return false;
      }
      return true;
    });
  };

  // Validate and filter media from a site's media array or searchableMediaItems
  const getFilteredMedia = (site) => {
    // Check if this is a searchable site with pre-processed media items
    if (Array.isArray(site.searchableMediaItems)) {
      let imageMedia = site.searchableMediaItems.filter(media => {
        if (!media) return false;
        return media.type === 'image' && media.url_thumbnail && media.url_thumbnail.match(/^https?:\/\/.+/);
      });

      // If feature filters are active, further filter by those features respecting AND/OR logic
      if (filters.features && filters.features.length > 0) {
        if (filters.featureFilterLogic === 'OR') {
          // OR: media must have at least one of the selected features
          imageMedia = imageMedia.filter(mediaItem => 
            filters.features.some(standardFeatureId => 
              Array.isArray(mediaItem.featureTags) && mediaItem.featureTags.includes(standardFeatureId)
            )
          );
        } else {
          // AND: media must have all of the selected features
          imageMedia = imageMedia.filter(mediaItem => 
            filters.features.every(standardFeatureId => 
              Array.isArray(mediaItem.featureTags) && mediaItem.featureTags.includes(standardFeatureId)
            )
          );
        }
      }
      
      // Convert searchableMediaItems format to expected format for compatibility
      return imageMedia.map(item => ({
        id: item.mediaId,
        type: item.type,
        url: item.url_thumbnail,
        title: item.title,
        featureTags: item.featureTags || []
      }));
    }
    
    // Fallback to legacy media array processing
    const allMedia = site.media || site;
    if (!Array.isArray(allMedia)) return [];
    
    let imageMedia = allMedia.filter(media => {
      if (!media) return false;
      return media.type === 'image' && media.url && media.url.match(/^https?:\/\/.+/);
    });

    // If feature filters are active, further filter these images by those features respecting AND/OR logic
    if (filters.features && filters.features.length > 0) {
      if (filters.featureFilterLogic === 'OR') {
        // OR: media must have at least one of the selected features
        imageMedia = imageMedia.filter(mediaItem => 
          filters.features.some(standardFeatureId => mediaContainsStandardizedFeature(mediaItem, standardFeatureId))
        );
      } else {
        // AND: media must have all of the selected features
        imageMedia = imageMedia.filter(mediaItem => 
          filters.features.every(standardFeatureId => mediaContainsStandardizedFeature(mediaItem, standardFeatureId))
        );
      }
    }
    return imageMedia;
  };

  const getSystemFilteredMedia = getFilteredMedia; // Keep for SiteCard prop compatibility
  
  const handleMediaClick = (site, mediaItem, _originalIndexFromSiteCard) => {
    if (mediaItem?.type !== 'image' || !mediaItem?.url?.match(/^https?:\/\/.+/)) {
      console.log('Clicked on non-image media or invalid image, not opening modal:', mediaItem);
      return;
    }

    const allImagesMatchingFilters = getFilteredMedia(site);

    if (allImagesMatchingFilters.length === 0) {
      setSelectedSiteMedia([mediaItem]); // Display clicked image alone
      setSelectedMedia({ ...mediaItem, siteId: site.id });
      setSelectedMediaIndex(0);
      onOpen();
      return;
    }
    
    // Find index of clicked mediaItem within the filtered list
    const modalIndex = allImagesMatchingFilters.findIndex(img => img.id === mediaItem.id && img.url === mediaItem.url);

    if (modalIndex === -1) {
      // Clicked image does not match current feature filters. Display it alone.
      setSelectedSiteMedia([mediaItem]);
      setSelectedMedia({ ...mediaItem, siteId: site.id });
      setSelectedMediaIndex(0);
    } else {
      setSelectedSiteMedia(allImagesMatchingFilters);
      setSelectedMedia({ ...mediaItem, siteId: site.id });
      setSelectedMediaIndex(modalIndex);
    }
    onOpen();
  };

  const handleNavigate = (direction) => {
    setSelectedMediaIndex((prev) => {
      if (direction === 'next') {
        return (prev + 1) % selectedSiteMedia.length;
      }
      return (prev - 1 + selectedSiteMedia.length) % selectedSiteMedia.length;
    });
    const nextMedia = selectedSiteMedia[selectedMediaIndex];
    if (!nextMedia?.type || !nextMedia?.url || (nextMedia.type === 'image' && !nextMedia.url.match(/^https?:\/\/.+/))) {
      console.error('Invalid media item:', nextMedia);
      return;
    }
    const siteId = selectedMedia.siteId;
    setSelectedMedia({ ...nextMedia, siteId });
  };

  const filteredSites = applyFilters(sites);
  
  const handleFilterChange = (newFilters) => {
    console.log('[Search.jsx] handleFilterChange - newFilters:', newFilters);
    setFilters(prev => {
      const updated = { ...prev, ...newFilters };
      console.log('[Search.jsx] handleFilterChange - updated filters state:', updated);
      return updated;
    });
  };
  
  const resetFilters = () => {
    setFilters({
      materialType: '',
      features: [], // Standardized IDs
      era: '',
      verificationStatus: '', // Consider removing if not used
      countries: [],
      featureFilterLogic: 'AND' // Reset logic
    });
  };

  // Handles feature toggling using standardized IDs
  const handleFeatureToggle = (featureOrId) => {
    // featureOrId is expected to be a standardized ID string or an object { id: 'standard_id', ... }
    const standardId = typeof featureOrId === 'string' ? featureOrId : featureOrId.id;

    if (!standardId) {
      console.warn('Attempted to toggle an undefined feature ID.');
      return;
    }
    
    const currentFeatures = filters.features || [];
    const exists = currentFeatures.includes(standardId);
    
    let newFeatures;
    if (exists) {
      newFeatures = currentFeatures.filter(id => id !== standardId);
    } else {
      newFeatures = [...currentFeatures, standardId];
    }
    
    console.log(`Toggle feature: ${standardId}, exists: ${exists}, new features:`, newFeatures);
    setFilters(prev => ({ ...prev, features: newFeatures }));
  };
  
  // Calculate active filters count, excluding featureFilterLogic
  const activeFilterCount = Object.entries(filters).reduce((count, [key, value]) => {
    if (key === 'featureFilterLogic') return count;
    if (Array.isArray(value)) return count + value.length;
    if (value) return count + 1;
    return count;
  }, 0);

  const hasActiveFilters = activeFilterCount > 0;

  const skeletonCards = Array(8).fill(0).map((_, i) => (
    <Box 
      key={`skeleton-${i}`} 
      bg="ancient.papyrus" 
      borderRadius="xl" 
      overflow="hidden" 
      boxShadow="0 2px 4px rgba(0,0,0,0.1)"
      border="1px solid"
      borderColor="ancient.mapBorder"
      className="map-texture"
    >
      <Skeleton height="200px" width="100%" />
      <Box p={5}>
        <VStack align="stretch" spacing={4}>
          <Skeleton height="24px" width="70%" />
          <Skeleton height="20px" width="90%" />
          <Skeleton height="16px" width="60%" />
          <Skeleton height="16px" width="80%" />
        </VStack>
      </Box>
    </Box>
  ));

  // Get feature label using standardized ID from featuresMetadata
  const getFeatureLabel = (standardFeatureId) => {
    if (featuresMetadata && featuresMetadata[standardFeatureId]) {
      return featuresMetadata[standardFeatureId].label || standardFeatureId;
    }
    // Fallback if not in metadata (should be rare with new metadata population)
    // This fallback could be improved to use mapFeature if needed, but ideally metadata is complete.
    return standardFeatureId;
  };

  return (
    <Box minH="100vh" bg="ancient.background" className="map-texture">
      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <Box
            borderRadius="xl"
            bg="ancient.papyrus"
            p={6}
            boxShadow="0 2px 4px rgba(0,0,0,0.1)"
            border="1px solid"
            borderColor="ancient.mapBorder"
            className="map-texture"
          >
            <VStack align="stretch" spacing={6}>
              <Heading as="h1" size={{ base: 'lg', md: 'xl' }} fontWeight="bold" color="ancient.mapText" fontFamily="heading"> {/* Responsive size */}
                Discover Ancient Sites
              </Heading>
              {name ? (
                <HStack>
                  <Icon as={TextAa} color="ancient.terracotta" boxSize={5} />
                  <Text color="ancient.ink" fontSize={{ base: 'md', md: 'lg' }}> {/* Responsive font */}
                    Showing results for name: <Text as="span" fontWeight="semibold" color="ancient.mapText">{name}</Text>
                  </Text>
                </HStack>
              ) : location && (
                <HStack>
                  <Icon as={MapPin} color="ancient.terracotta" boxSize={5} />
                  <Text color="ancient.ink" fontSize={{ base: 'md', md: 'lg' }}> {/* Responsive font */}
                    Showing results near: <Text as="span" fontWeight="semibold" color="ancient.mapText">{location}</Text>
                  </Text>
                </HStack>
              )}
              <SearchBar initialValue={location || ''} />
              <HStack justify="space-between" mt={2}>
                <HStack spacing={2}>
                  <Button
                    leftIcon={<Icon as={Funnel} />}
                    variant="outline"
                    color="ancient.terracotta"
                    borderColor="ancient.mapBorder"
                    size="sm"
                    onClick={() => setIsFiltersVisible(!isFiltersVisible)}
                    _hover={{
                      bg: 'ancient.lightStone'
                    }}
                  >
                    {isFiltersVisible ? "Hide Filters" : "Show Filters"}
                  </Button>
                  {hasActiveFilters && (
                    <Badge colorScheme="blue" variant="subtle">
                      {activeFilterCount} active
                    </Badge>
                  )}
                </HStack>
                {/* Link to Elasticsearch Search Page */}
                <Button
                  as={RouterLink}
                  to={{
                    pathname: "/elasticsearch-search", // Updated path
                    search: location ? `?location=${encodeURIComponent(location)}` : name ? `?name=${encodeURIComponent(name)}` : ""
                  }}
                  leftIcon={<Icon as={MagnifyingGlass} />}
                  variant="outline"
                  color="ancient.terracotta"
                  borderColor="ancient.mapBorder"
                  size="sm"
                  _hover={{ bg: 'ancient.lightStone' }}
                >
                  Elasticsearch Search
                </Button>
              </HStack>
              {hasActiveFilters && (
                <Box>
                  <HStack mb={2}>
                    <Text fontSize={{ base: 'xs', md: 'sm' }} color="ancient.ink" fontWeight="medium">Active filters:</Text>
                  </HStack>
                  <Wrap spacing={2}>
                    {/* Display Feature Filter Logic if features are selected */}
                    {filters.features && filters.features.length > 0 && (
                      <WrapItem>
                        <Badge
                          bg={useColorModeValue('purple.100', 'purple.700')}
                          color={useColorModeValue('gray.800', 'whiteAlpha.900')}
                          borderRadius="full"
                          px={3}
                          py={1.5}
                          fontSize={{ base: 'xs', md: 'sm' }}
                          border="1px solid"
                          borderColor={useColorModeValue('purple.300', 'purple.500')}
                        >
                          Features: {filters.featureFilterLogic}
                        </Badge>
                      </WrapItem>
                    )}
                    {filters.materialType && (
                      <WrapItem>
                        <Badge 
                          bg="ancient.lightStone" 
                          color="ancient.mapText" 
                          borderRadius="full" 
                          px={3} 
                          py={1.5}
                          display="flex"
                          alignItems="center"
                          fontSize={{ base: 'xs', md: 'sm' }}
                          border="1px solid"
                          borderColor="ancient.mapBorder"
                        >
                          <Box mr={1}>Material: {filters.materialType}</Box>
                          <Icon as={X} cursor="pointer" boxSize={3} onClick={() => handleFilterChange({ materialType: '' })} />
                        </Badge>
                      </WrapItem>
                    )}
                    {filters.era && (
                      <WrapItem>
                        <Badge 
                          bg="ancient.lightStone" 
                          color="ancient.mapText" 
                          borderRadius="full" 
                          px={3} 
                          py={1.5}
                          display="flex"
                          alignItems="center"
                          fontSize={{ base: 'xs', md: 'sm' }} // Responsive font
                          border="1px solid"
                          borderColor="ancient.mapBorder"
                        >
                          <Box mr={1}>Era: {filters.era}</Box>
                          <Icon as={X} cursor="pointer" boxSize={3} onClick={() => handleFilterChange({ era: '' })} />
                        </Badge>
                      </WrapItem>
                    )}
                    {filters.features.map(standardFeatureId => (
                      <WrapItem key={standardFeatureId}>
                        <Tooltip label={`Remove "${getFeatureLabel(standardFeatureId)}" filter`} placement="top" hasArrow>
                          <Badge 
                            bg="ancient.lightStone" 
                            color="ancient.mapText" 
                            px={3} 
                            py={1.5} 
                            borderRadius="full"
                            cursor="pointer"
                            fontSize={{ base: 'xs', md: 'sm' }}
                            display="flex" 
                            alignItems="center" 
                            onClick={() => handleFeatureToggle(standardFeatureId)} // Pass standard ID directly
                            _hover={{ bg: 'ancient.stone' }}
                            border="1px solid"
                            borderColor="ancient.mapBorder"
                          >
                            <Box mr={1}>{getFeatureLabel(standardFeatureId)}</Box>
                            <Icon as={X} boxSize={3} />
                          </Badge>
                        </Tooltip>
                      </WrapItem>
                    ))}
                    {filters.countries?.map(country => (
                      <WrapItem key={country}>
                        <Badge 
                          bg="ancient.lightStone" 
                          color="ancient.mapText" 
                          borderRadius="full" 
                          px={3} 
                          py={1.5}
                          display="flex"
                          alignItems="center"
                          fontSize={{ base: 'xs', md: 'sm' }} // Responsive font
                          border="1px solid"
                          borderColor="ancient.mapBorder"
                        >
                          <Box mr={1}>{country}</Box>
                          <Icon as={X} cursor="pointer" boxSize={3} onClick={() => {
                            const newCountries = filters.countries.filter(c => c !== country);
                            handleFilterChange({ countries: newCountries });
                          }} />
                        </Badge>
                      </WrapItem>
                    ))}
                    <WrapItem>
                      <Button 
                        size="xs" 
                        color="ancient.terracotta" 
                        borderColor="ancient.mapBorder" 
                        variant="outline" 
                        onClick={resetFilters}
                        _hover={{
                          bg: 'ancient.lightStone'
                        }}
                      >
                        Reset All
                      </Button>
                    </WrapItem>
                  </Wrap>
                </Box>
              )}
            </VStack>
          </Box>
          <Grid 
            templateColumns={{ 
              base: "1fr", 
              lg: isFiltersVisible ? "280px 1fr" : "1fr" 
            }} 
            gap={6}
          >
            {isFiltersVisible && (
            <GridItem>
              <Fade in={isFiltersVisible} unmountOnExit>
                <Box 
                  display={{ base: isFiltersVisible ? "block" : "none", lg: "block" }} // Keep lg:block for when it's part of the grid
                  bg="ancient.papyrus"
                  p={4}
                  borderRadius="xl"
                  boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                  border="1px solid"
                  borderColor="ancient.mapBorder"
                  className="map-texture"
                >
                  <SearchFilters 
                    filters={{...filters, featuresMetadata}} 
                    onFilterChange={handleFilterChange} 
                    onReset={resetFilters}
                    onInfoClick={onInfoModalOpen} // Pass handler for info modal
                    enableElasticsearchAggregations={false} // Explicitly disable ES fetch
                    filterOptions={{ countries: availableCountries.map(country => ({ id: country, label: country })) }}
                  />
                </Box>
              </Fade>
            </GridItem>
            )}
            <GridItem>
              <VStack align="stretch" spacing={6}>
                <HStack 
                  justify="space-between" 
                  bg="ancient.papyrus" 
                  p={4} 
                  borderRadius="xl" 
                  boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                  border="1px solid"
                  borderColor="ancient.mapBorder"
                  className="map-texture"
                >
                  <HStack flexGrow={1} justify="space-between" w="100%">
                    <HStack>
                      <Icon as={MagnifyingGlass} color="ancient.terracotta" />
                      <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight="medium" color="ancient.mapText">
                        {isLoading ? 'Searching...' : `${filteredSites.length} ${filteredSites.length === 1 ? 'site' : 'sites'} found`}
                      </Text>
                    </HStack>
                    
                    {!isLoading && filteredSites.length > 0 && (
                      <ButtonGroup size="sm" isAttached variant="outline">
                        <IconButton
                          icon={<Text fontSize="sm">1</Text>}
                          aria-label="Single column layout"
                          onClick={() => setGridColumns(1)}
                          isActive={gridColumns === 1}
                          color="ancient.terracotta"
                          borderColor="ancient.mapBorder"
                          _hover={{ bg: 'ancient.lightStone' }}
                          _active={{ bg: 'ancient.stone' }}
                        />
                        <IconButton
                          icon={<Text fontSize="sm">2</Text>}
                          aria-label="Two column layout"
                          onClick={() => setGridColumns(2)}
                          isActive={gridColumns === 2}
                          color="ancient.terracotta"
                          borderColor="ancient.mapBorder"
                          _hover={{ bg: 'ancient.lightStone' }}
                          _active={{ bg: 'ancient.stone' }}
                        />
                        <IconButton
                          icon={<Text fontSize="sm">3</Text>}
                          aria-label="Three column layout"
                          onClick={() => setGridColumns(3)}
                          isActive={gridColumns === 3}
                          color="ancient.terracotta"
                          borderColor="ancient.mapBorder"
                          _hover={{ bg: 'ancient.lightStone' }}
                          _active={{ bg: 'ancient.stone' }}
                        />
                      </ButtonGroup>
                    )}
                  </HStack>
                </HStack>
                {isLoading ? (
                  <SimpleGrid 
                    columns={{ 
                      base: 1, 
                      sm: gridColumns === 1 ? 1 : 2, // Adjust for smaller screens
                      lg: gridColumns 
                    }} 
                    spacing={6}
                  >
                    {skeletonCards}
                  </SimpleGrid>
                ) : error ? (
                  <Box 
                    textAlign="center" 
                    p={10} 
                    bg="ancient.papyrus" 
                    borderRadius="xl" 
                    boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                    border="1px solid"
                    borderColor="ancient.mapBorder"
                    className="map-texture"
                  >
                    <Heading size={{ base: 'sm', md: 'md' }} color="ancient.terracotta" mb={4}>{error}</Heading> {/* Responsive size */}
                    <Text mb={6} color="ancient.ink" fontSize={{ base: 'sm', md: 'md' }}>There was an issue retrieving the megalithic sites. Please try again later.</Text> {/* Responsive font */}
                    <Button 
                      color="ancient.terracotta" 
                      borderColor="ancient.mapBorder" 
                      variant="outline" 
                      onClick={() => window.location.reload()}
                      _hover={{
                        bg: 'ancient.lightStone'
                      }}
                    >
                      Retry
                    </Button>
                  </Box>
                ) : filteredSites.length > 0 ? (
                  <Fade in={!isLoading}>
                    <SimpleGrid 
                      columns={{ 
                        base: 1, 
                        sm: gridColumns === 1 ? 1 : 2, // Adjust for smaller screens
                        lg: gridColumns 
                      }} 
                      spacing={6}
                    >
                      {filteredSites.map(site => {
                        // Convert searchable site data to format expected by SiteCard
                        const siteWithMedia = {
                          ...site,
                          // Convert searchableMediaItems to expected media format
                          media: Array.isArray(site.searchableMediaItems)
                            ? site.searchableMediaItems.map(item => ({
                                id: item.mediaId,
                                type: item.type,
                                url: item.url_thumbnail,
                                title: item.title,
                                // Use the 'predictions' field directly from searchableMediaItem,
                                // which now contains full prediction objects with coordinates.
                                predictions: item.predictions || [] 
                              }))
                            : (site.media || [])
                        };

                        return (
                          <SiteCard
                            key={site.id} 
                            site={siteWithMedia} 
                            onMediaClick={(mediaItem, index) => handleMediaClick(siteWithMedia, mediaItem, index)}
                            showFilteredMedia={filters.features?.length > 0}
                            filteredMedia={getFilteredMedia(siteWithMedia)}
                            onFeatureFilter={(feature) => handleFeatureToggle(feature)} // Uses updated handleFeatureToggle
                            filters={{...filters, featuresMetadata, featureFilterLogic: filters.featureFilterLogic }} // Pass logic
                          />
                        );
                      })}
                    </SimpleGrid>
                  </Fade>
                ) : (
                  <Fade in={!isLoading}>
                    <Box 
                      textAlign="center" 
                      py={16} 
                      px={8} 
                      bg="ancient.papyrus" 
                      borderRadius="xl" 
                      boxShadow="0 2px 4px rgba(0,0,0,0.1)"
                      border="1px solid"
                      borderColor="ancient.mapBorder"
                      className="map-texture"
                    >
                      <Heading size={{ base: 'sm', md: 'md' }} mb={3} color="ancient.mapText">No sites found matching your criteria</Heading> {/* Responsive size */}
                      <Text color="ancient.ink" mb={6} fontSize={{ base: 'sm', md: 'md' }}>Try adjusting your filters or searching for a different location</Text> {/* Responsive font */}
                      <Button 
                        color="ancient.terracotta" 
                        borderColor="ancient.mapBorder" 
                        variant="outline" 
                        onClick={resetFilters} 
                        leftIcon={<Icon as={Funnel} />}
                        _hover={{
                          bg: 'ancient.lightStone'
                        }}
                      >
                        Reset Filters
                      </Button>
                    </Box>
                  </Fade>
                )}
              </VStack>
            </GridItem>
          </Grid>
        </VStack>
      </Container>
      <ImagePreviewModal
        isOpen={isOpen}
        onClose={onClose}
        imageUrl={selectedMedia?.url}
        onPrevious={() => handleNavigate('prev')}
        onNext={() => handleNavigate('next')}
        hasMultiple={selectedSiteMedia.length > 1}
        currentImage={selectedMedia}
        onFeaturesUpdated={(predictions) => { // This might need adjustment if feature structure in modal changes
          setSelectedMedia(prev => ({ ...prev, predictions }));
          const updatedMedia = [...selectedSiteMedia];
          const mediaIndex = selectedMediaIndex;
          if (mediaIndex >= 0 && mediaIndex < updatedMedia.length) {
            updatedMedia[mediaIndex] = { ...updatedMedia[mediaIndex], predictions };
          }
          setSelectedSiteMedia(updatedMedia);
        }}
      />

      {/* Feature Filter Logic Info Modal (copied from SearchElasticsearch.jsx) */}
      <Modal isOpen={isInfoModalOpen} onClose={onInfoModalClose} isCentered motionPreset="slideInBottom">
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(5px)" /> 
        <ModalContent 
          bg={useColorModeValue('white', 'gray.800')} 
          color={useColorModeValue('gray.800', 'whiteAlpha.900')} 
          borderRadius="xl" 
          border="1px solid" 
          borderColor={useColorModeValue('gray.200', 'gray.700')}
        >
          <ModalHeader fontFamily="heading" fontWeight="bold" color={useColorModeValue('gray.700', 'white')}>Feature Filtering Logic</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color={useColorModeValue('gray.700', 'whiteAlpha.800')}>
                When you select multiple features to filter by, you can choose how they are combined:
              </Text>
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue('gray.50', 'gray.700')}>
                <Heading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>AND Logic (Default)</Heading>
                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                  Shows sites where media items contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ALL</Text> of the selected features. This is more restrictive and helps you find very specific results.
                </Text>
              </Box>
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor={useColorModeValue('gray.200', 'gray.600')} bg={useColorModeValue('gray.50', 'gray.700')}>
                <Heading size="sm" mb={1} color={useColorModeValue('blue.600', 'blue.300')}>OR Logic</Heading>
                <Text fontSize="sm" color={useColorModeValue('gray.600', 'gray.300')}>
                  Shows sites where media items contain <Text as="strong" color={useColorModeValue('gray.700', 'gray.100')}>ANY</Text> of the selected features. This is less restrictive and broadens your search.
                </Text>
              </Box>
              <Text fontSize="xs" color="ancient.ink.subtle">
                This logic applies to the features found within individual photos, videos, or 3D models associated with a site.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="outline" 
              color="ancient.terracotta" 
              borderColor="ancient.mapBorder" 
              onClick={onInfoModalClose}
              _hover={{ bg: 'ancient.lightStone' }}
            >
              Got it
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
